import {
  convertToHumanReadable,
  convertWeightToHumanReadable,
  percentOf,
  siteColors,
} from "@/Utilities/Utilities";
import { Chart } from "@/Components";
import React, { useMemo, useEffect, useState } from "react";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { SCOPE_OPTIONS } from "@/Enums/scopes";
import { BarWithErrorBarsController } from "chartjs-chart-error-bars";

export const chartColors = [
  "#5D4634",
  "#7E9CB3",
  "#F96733",
  "#988BCB",
  // "#DDEBFC",
  "#007350",
  "#E9CE2C",
  "#AD6717",
  "#69A197",
  "#E5F993",
  "#FF9D00",
  "#7F78AB",
  // siteColors.primary,
  // siteColors.secondary,
  // siteColors.tertiary,
  // "#004561",
  // siteColors.primaryLight,
  // "#668E9F",
  // "#FFD17C",
  // "#AE3968",
  // "#639C40",
  // "#79A4CF",
  // "#DF7784",
  // "#806B6F",
];

export const getChartColor = (index, label) => {
  let i =
    {
      "Scope 1": 0,
      "Scope 2": 1,
      "Scope 3": 2,
      "Out of scope": -2,
      Trend: 4,
      "Paris Agreement": -1,
      "Uncategorized emissions": -1,
    }[label] || index % chartColors.length;
  if (i === -1) return "#DDEBFC"; // color not in normal graph colors...
  if (i === -2) return "#C4C4C4"; // color not in normal graph colors...
  return chartColors[i];
};

export const chartTextColors = [
  "white",
  "black",
  "white",
  "white",
  "black",
  "white",
  "black",
  "black",
  "black",
  "white",
];

export const DashboardPieChart = ({
  dataSets,
  size = { width: 350, height: 350 },
  className = "",
  emission = true,
}) => {
  const backgroundColors = useMemo(() => {
    return dataSets.map((set, i) => set.backgroundColor);
  }, [dataSets]);

  const textColors = useMemo(() => {
    return dataSets.map((set, i) => set.textColor);
  }, [dataSets]);

  const labels = useMemo(() => {
    return dataSets.map((set) => set.label);
  }, [dataSets]);

  const units = useMemo(() => {
    return dataSets.map((set) => set.unit);
  }, [dataSets]);

  const data = useMemo(() => {
    return dataSets.map((set) => set.data.reduce((a, b) => a + b, 0));
  }, [dataSets]);

  const getTotal = (data) => {
    return data.labels.reduce((acc, label, index) => {
      const sum = data.datasets.reduce(
        (sum, dataset) => sum + dataset.data[index],
        0,
      );
      return acc + sum;
    }, 0);
  };

  return (
    <>
      <Chart
        width={size?.width || 350}
        height={size?.height || 350}
        className={`max-w-[350px] ${className}`}
        config={{
          type: "pie",
          data: {
            labels: labels,
            datasets: [
              {
                label: `KG`,
                borderJoinStyle: "round",
                hoverOffset: 4,
                borderRadius: 8,
                borderWidth: 4,
                backgroundColor: backgroundColors,
                datalabels: {
                  color: textColors,
                },
                data: data,
              },
            ],
            units: units, // Custom
          },
          options: {
            plugins: {
              datalabels: {
                formatter: function (value, context) {
                  const chart = context.chart;
                  const total = getTotal(chart.data);

                  return value === 0 || value === ""
                    ? ""
                    : `${labels[context.dataIndex]}\n${percentOf(
                        value,
                        total,
                      )} %`;
                },
              },
              legend: {
                display: false,
              },
              tooltip: {
                padding: 15,
                displayColors: false,
                backgroundColor: (context) => {
                  return context.tooltip.labelColors[0].backgroundColor;
                },
                titleColor: (context) => {
                  return textColors?.[context?.tooltipItems?.[0]?.dataIndex];
                },
                callbacks: {
                  title: (context) => {
                    const chart = context[0].chart;
                    const total = getTotal(chart.data);

                    if (emission) {
                      return `Total: ${
                        convertWeightToHumanReadable(total)?.concat
                      } CO₂e`;
                    }

                    return context[0].label;
                  },
                  labelTextColor: (context) => {
                    return textColors[context.dataIndex];
                  },
                  label: (context) => {
                    const chart = context.chart;
                    const index = context.dataIndex;
                    const datasetIndex = context.datasetIndex;
                    const value = chart.data.datasets[datasetIndex].data[index];
                    if (emission) {
                      return `${
                        convertWeightToHumanReadable(value)?.concat
                      } CO₂e`;
                    }

                    return `${convertToHumanReadable(value)} ${units?.[index]}`;
                  },
                },
              },
            },
          },
        }}
        plugins={[ChartDataLabels]}
      />
    </>
  );
};

export const DashboardBarChart = ({
  dataSets,
  labels,
  disableAnimations = false,
  showTotalAboveBar = false,
  onChartUpdated = (_) => null,
}) => {
  const legendSpacingPlugin = {
    id: "increase-legend-spacing",
    beforeInit(chart) {
      // Get reference to the original fit function
      const originalFit = chart.legend.fit;

      // Override the fit function
      chart.legend.fit = function fit() {
        // Call original function and bind scope in order to use `this` correctly inside it
        originalFit.bind(chart.legend)();
        // Change the height as suggested in another answers
        this.height += 20;
      };
    },
  };

  let shouldDisplayTonnes = false;

  shouldDisplayTonnes = dataSets.some((set) =>
    set.data.some((value) => Math.abs(value.y) > 1000),
  );

  /**
   * For some reason ChartJS is dogshit and cannot always compute the max value of the y-axis for a stacked bar chart
   * So we have to do it manually and provide them with a suggested max value
   *
   * Find the maximum value in the dataset to set the y-axis max value
   * @type {number}
   */
  let maxValue = Math.round(
    labels.reduce((acc, label, index) => {
      const sum = dataSets.reduce(
        (sum, dataset) => sum + dataset.data[index].y,
        0,
      );
      return Math.max(acc, sum);
    }, 0),
  );

  return (
    <Chart
      onChartUpdated={onChartUpdated}
      height={350}
      width={500}
      plugins={[legendSpacingPlugin, ChartDataLabels]}
      config={{
        disableAnimations: disableAnimations,
        type: BarWithErrorBarsController.id,
        // type: "bar",
        data: {
          labels: labels,
          datasets: dataSets,
        },
        elements: {
          point: {
            radius: 2,
          },
        },
        options: {
          scales: {
            x: {
              stacked: true,
              grid: {
                display: false,
              },
            },
            y: {
              suggestedMax: maxValue,
              stacked: true,
              ticks: {
                callback: (tick) => {
                  return shouldDisplayTonnes ? tick / 1000 : tick;
                },
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
              text: `${shouldDisplayTonnes ? "TONNES" : "KG"} CO₂e`,
              align: "start",
            },
            datalabels: {
              align: "end",
              anchor: "end",
              color: "black",
              font: {
                weight: "bold",
              },
              display: function (context) {
                // Only display on the last dataset
                return (
                  showTotalAboveBar &&
                  context.datasetIndex ===
                    context.chart.data.datasets.length - 1
                );
              },
              formatter: function (value, context) {
                const chart = context.chart;
                const index = context.dataIndex;
                const total = chart.data.datasets.reduce(
                  (acc, dataset) => acc + dataset.data[index].y,
                  0,
                );
                return convertWeightToHumanReadable(total, true)?.concat;
              },
            },
            tooltip: {
              padding: 15,
              displayColors: false,
              backgroundColor: (context) => {
                return context.tooltip.labelColors[0].backgroundColor;
              },
              titleColor: (context) => {
                return context.tooltip.labelTextColors[0];
              },
              callbacks: {
                label: function (context) {
                  const chart = context.chart;
                  const index = context.dataIndex;
                  const datasetIndex = context.datasetIndex;
                  const value = chart.data.datasets[datasetIndex].data[index].y;
                  return `${context.dataset.label}: ${
                    convertWeightToHumanReadable(value)?.concat
                  } CO₂e`;
                },
                title: function (context) {
                  const chart = context[0].chart;
                  const index = context[0].dataIndex;
                  const total = chart.data.datasets.reduce(
                    (acc, dataset) => acc + dataset.data[index].y,
                    0,
                  );
                  return `Total: ${
                    convertWeightToHumanReadable(total)?.concat
                  } CO₂e`;
                },
              },
            },
          },
        },
      }}
    />
  );
};

export const DashboardLineChart = ({ dataSets, labels }) => {
  const legendSpacingPlugin = {
    id: "increase-legend-spacing",
    beforeInit(chart) {
      // Get reference to the original fit function
      const originalFit = chart.legend.fit;

      // Override the fit function
      chart.legend.fit = function fit() {
        // Call original function and bind scope in order to use `this` correctly inside it
        originalFit.bind(chart.legend)();
        // Change the height as suggested in another answers
        this.height += 20;
      };
    },
  };

  // Ensure datasets have matching line and dot colors
  dataSets = dataSets.map((set) => ({
    ...set,
    borderColor: set.backgroundColor, // Ensuring line color matches dot color
    pointBackgroundColor: set.backgroundColor, // Ensuring dot color is set
  }));

  let shouldDisplayTonnes = false;

  shouldDisplayTonnes = dataSets.some((set) =>
    set.data.some((value) => Math.abs(value) > 1000),
  );

  // Set any data values that's zero to null as its not been reported any emissions
  dataSets.forEach((set) => {
    set.data = set.data.map((value) => (value === 0 ? null : value));
  });

  return (
    <Chart
      height={350}
      width={500}
      plugins={[legendSpacingPlugin]}
      config={{
        type: "line",
        data: {
          labels: labels,
          datasets: dataSets,
        },
        elements: {
          point: {
            radius: 2,
          },
        },
        options: {
          scales: {
            x: {
              beginAtZero: true,
              grid: {
                display: false,
              },
            },
            y: {
              ticks: {
                callback: (tick) => {
                  return shouldDisplayTonnes ? tick / 1000 : tick;
                },
              },
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            title: {
              display: true,
              text: `${shouldDisplayTonnes ? "TONNES" : "KG"} CO₂`,
              align: "start",
            },
            tooltip: {
              padding: 15,
              displayColors: false,
              backgroundColor: (context) => {
                return context.tooltip.labelColors[0].backgroundColor;
              },
              titleColor: (context) => {
                return context.tooltip.labelTextColors[0];
              },
              callbacks: {
                label: function (context) {
                  const chart = context.chart;
                  const index = context.dataIndex;
                  const datasetIndex = context.datasetIndex;
                  const value = chart.data.datasets[datasetIndex].data[index];
                  return `${context.dataset.label}: ${
                    convertWeightToHumanReadable(value)?.concat
                  } CO₂e`;
                },
                title: function (context) {
                  const chart = context[0].chart;
                  const index = context[0].dataIndex;
                  const total = chart.data.datasets.reduce(
                    (acc, dataset) => acc + dataset.data[index],
                    0,
                  );
                  return `Total: ${
                    convertWeightToHumanReadable(total)?.concat
                  } CO₂e`;
                },
              },
            },
          },
        },
      }}
    />
  );
};

export const BudgetChart = ({
  dataSets,
  labels,
  exceedYear,
  disableAnimations = false,
  onChartUpdated = (_) => null,
}) => {
  let shouldDisplayTonnes = false;
  const [actualDataSet, setActualDataSet] = useState(dataSets);

  // Go through the datasets where label is in SCOPE_OPTIONS, check if any values are larger than 1000. Then we should display values in tonnes instead of kg
  SCOPE_OPTIONS.forEach((scope) => {
    if (shouldDisplayTonnes) return;
    const set = dataSets.find((row) => row.label === scope.label);
    if (set) {
      // Check if we should display tonnes instead of kg
      if (set.data.some((value) => value > 1000 || value < -1000)) {
        shouldDisplayTonnes = true;
      }
    }
  });

  useEffect(() => {
    const timer = setTimeout(() => {
      setActualDataSet(dataSets);
    }, 500);

    return () => clearTimeout(timer);
  }, [dataSets]);

  const chartAreaBorder = {
    id: "chartAreaBorder",
    afterDatasetsDraw(chart, args, options) {
      // get id of trend dataset
      const trendId = chart.data.datasets.findIndex(
        (dataset) => dataset.label === "Trend",
      );
      if (!trendId || trendId === -1) return;
      var meta = chart.getDatasetMeta(trendId);

      var points = [];
      meta.data.forEach(function (element, index) {
        if (!element.skip) {
          var point = element.getProps(["x", "y"]);
          points.push(point);
        }
      });

      if (points.length < 3) return;

      const {
        ctx,
        chartArea: { left, top, width, height, bottom },
      } = chart;
      ctx.save();
      ctx.strokeStyle = options.borderColor;
      ctx.lineWidth = options.borderWidth;
      ctx.setLineDash(options.borderDash || []);
      ctx.lineDashOffset = options.borderDashOffset;

      ctx.beginPath();
      ctx.moveTo(points[points.length - 1].x + 1, points[points.length - 1].y);
      ctx.lineTo(points[points.length - 1].x + 1, bottom);
      ctx.lineTo(points[0].x, bottom);
      ctx.lineTo(points[0].x, points[0].y);
      ctx.stroke();

      ctx.restore();
    },
  };

  return (
    <Chart
      onChartUpdated={onChartUpdated}
      height={450}
      width={500}
      config={{
        disableAnimations: disableAnimations,
        type: "line",
        data: {
          labels: labels,
          datasets: actualDataSet,
        },
        options: {
          responsive: true,
          title: {
            display: true,
            text: "Historical CO2 Emissions vs Paris Agreement Target",
          },
          interaction: {
            mode: "nearest",
            axis: "x",
            intersect: false,
          },
          scales: {
            x: {
              display: true,
              scaleLabel: {
                display: true,
                labelString: "Year",
              },
              grid: {
                display: false,
              },
              ticks: {
                callback: (_, i) => {
                  return i % 2 === new Date().getFullYear() % 2
                    ? labels[i]
                    : null;
                },
              },
            },
            y: {
              stacked: true,
              display: true,
              scaleLabel: {
                display: true,
                labelString: "CO2 Emissions",
              },
              ticks: {
                callback: (tick) => {
                  return shouldDisplayTonnes ? tick / 1000 : tick;
                },
              },
            },
          },
          plugins: {
            chartAreaBorder: {
              borderColor: "#868686",
              borderWidth: 2,
              borderDash: [5, 5],
              borderDashOffset: 2,
            },
            legend: {
              position: "bottom",
              align: "start",
              labels: {
                boxWidth: 10,
                boxHeight: 10,
              },
            },
            title: {
              display: true,
              text: `${shouldDisplayTonnes ? "TONNES" : "KG"} CO₂e`,
              align: "start",
              padding: {
                top: 0,
                bottom: 20,
              },
            },
            tooltip: {
              padding: 15,
              displayColors: false,
              titleColor: (context) => {
                return context.tooltip.labelTextColors[0];
              },
              callbacks: {
                // footer: (tooltipItems) => {
                //   let sum = 0;
                //   tooltipItems.forEach(function(tooltipItem) {
                //     sum += tooltipItem.parsed.y;
                //   });
                //   return 'Sum: ' + convertWeightToHumanReadable(sum)?.concat;
                // },
                label: function (context) {
                  const chart = context.chart;
                  const index = context.dataIndex;
                  const datasetIndex = context.datasetIndex;
                  const value = chart.data.datasets[datasetIndex].data[index];
                  return `${context.dataset.label}: ${
                    convertWeightToHumanReadable(value)?.concat
                  } CO₂e`;
                },
                title: function (context) {
                  const chart = context[0].chart;
                  const index = context[0].dataIndex;
                  const yearLabel = chart.data.labels[index];
                  const currentYear = new Date().getFullYear();

                  if (yearLabel === currentYear) {
                    return `Prediction for ${currentYear} based on previous years`;
                  }
                  if (exceedYear && yearLabel > currentYear) {
                    return `If the trend continues your budget ends ${exceedYear}`;
                  }
                },
              },
            },
          },
        },
      }}
      plugins={[chartAreaBorder]}
    />
  );
};
